import { alpha } from "@mui/material/styles";
import { Autocomplete, Divider, Paper, TextField } from "@mui/material";
import Box from "@mui/material/Box/Box";
import { ClickAwayListener } from "@mui/base";
import debounce from "lodash.debounce";
import React, { useState, useCallback, useEffect } from "react";

import { colors } from "styles/colors";
import { formatError } from "utils/strings";
import { AsyncSearchSelectV2Props } from "./AsyncSearchSelectV2.types";
import { theme } from "styles/theme";
import { Option } from "./AsyncSearchSelectV2.types";

const normalizeOptionValue = (option: Partial<Option> | null | undefined): string => {
  const rawValue = option?.value ?? option?.label ?? "";

  return typeof rawValue === "string" ? rawValue : String(rawValue);
};

const AsyncSearchSelectV2 = (props: AsyncSearchSelectV2Props): JSX.Element => {
  const {
    value,
    label,
    onChange,
    onBlur,
    fetchOptions,
    createOption,
    variant = "outlined",
    readOnly = false,
    disabled = false,
    required = false,
    noOptionsText = "No matching options",
    createNewOptionText = "Create new option",
    error = false,
    helperText = "",
    errorText = "",
    fullWidth = false,
    multiple = false,
    showPopupIcon = false,
    minSearchLength = 0,
  } = props;

  const [input, setInput] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [options, setOptions] = useState<Option[]>([]);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      try {
        setLoadingOptions(true);
        const response = await fetchOptions(search);
        setOptions(response);
        setLoadingOptions(false);
      } catch (e) {
        console.error(formatError(e));
        setOptions([]);
        setLoadingOptions(false);
      }
    })();
  }, [fetchOptions, search]);

  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
  const inputIsValidNewOption =
    input === search &&
    !loadingOptions &&
    !selectedValues.some(
      (selectedValue) => normalizeOptionValue(selectedValue).toLowerCase() === input.toLowerCase()
    ) &&
    !options.map((option) => normalizeOptionValue(option).toLowerCase()).includes(input.toLowerCase());

  const CustomPaper = (props: React.HTMLAttributes<HTMLElement>) => {
    return (
      <Paper
        elevation={8}
        {...props}
        sx={{ border: 1, borderColor: colors.lightGrey4 }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Box sx={{ maxHeight: "calc(100vh - 200px)", overflow: "hidden" }}>{props.children}</Box>
        <Divider />
        {createOption && input && (
          <Box
            onMouseDown={(e) => {
              e.preventDefault();
            }}
          >
            <Box
              sx={{
                paddingX: (theme) => theme.spacing(2),
                paddingY: (theme) => theme.spacing(1),
                fontSize: "13px",
              }}
            >
              {createNewOptionText}
            </Box>
            <Box
              sx={{
                paddingX: (theme) => theme.spacing(2),
                paddingY: (theme) => theme.spacing(1),
                ":hover": inputIsValidNewOption
                  ? {
                      backgroundColor: alpha(theme.palette.primary.light, 0.1),
                      cursor: "pointer",
                    }
                  : {},
              }}
              onMouseDown={async (e) => {
                e.preventDefault();
                if (inputIsValidNewOption) {
                  try {
                    const response = await createOption(input);
                    const createdOption = { label: input, value: response.data.id };
                    onChange(multiple ? [...selectedValues, createdOption] : createdOption);
                    setInput("");
                    setSearch("");
                    if (!multiple) {
                      setOpen(false);
                    }
                  } catch {
                    console.error("Could not create option");
                  }
                }
              }}
              color={inputIsValidNewOption ? colors.black : colors.disabledGrey}
            >
              {input}
            </Box>
          </Box>
        )}
      </Paper>
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateSearchString = useCallback(
    debounce((inputText: string) => {
      setSearch(inputText);
    }, 500),
    []
  );

  useEffect(() => {
    (async () => {
      if (input.length >= minSearchLength) {
        updateSearchString(input);
      }
    })();
  }, [input, minSearchLength, updateSearchString]);

  return (
    <ClickAwayListener
      onClickAway={() => {
        setOpen(false);
      }}
    >
      <Autocomplete<Option, boolean, false, false>
        open={open}
        onOpen={() => {
          if (!readOnly && !disabled) {
            setOpen(true);
          }
        }}
        onClose={() => {
          setOpen(false);
        }}
        multiple={multiple}
        disableCloseOnSelect={multiple}
        value={value as any}
        getOptionLabel={(option) => option.label}
        filterOptions={(x) => x}
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        inputValue={input}
        onInputChange={(_, newInput) => {
          setInput(newInput);
        }}
        onChange={(_, selectedOption) => {
          onChange(selectedOption as Option | Option[] | null);
          if (!multiple) {
            setOpen(false);
          }
        }}
        readOnly={readOnly}
        disabled={disabled}
        forcePopupIcon={showPopupIcon ? true : "auto"}
        popupIcon={showPopupIcon ? undefined : null}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.value}>
              {option.label}
            </li>
          );
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              value={input}
              label={label}
              onClick={(e) => {
                e.preventDefault();
                if (!readOnly && !disabled) {
                  setOpen(true);
                }
              }}
              onBlur={onBlur}
              onKeyDown={(event) => {
                if (event.key === "Backspace") {
                  event.stopPropagation();
                }
              }}
              variant={variant}
              disabled={disabled}
              error={error}
              helperText={errorText || helperText}
              required={required}
              fullWidth={fullWidth}
            />
          );
        }}
        options={options}
        noOptionsText={noOptionsText}
        PaperComponent={CustomPaper}
        fullWidth={fullWidth}
      />
    </ClickAwayListener>
  );
};

export default AsyncSearchSelectV2;
